(() => {
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const trapFocus = (container) => {
    const f = qsa('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])', container);
    const first = f[0], last = f[f.length - 1];
    container.addEventListener('keydown', container.__trap__ = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  };
  const releaseFocus = (container) => { if (container?.__trap__) container.removeEventListener('keydown', container.__trap__); };
  const debounce = (fn, ms = 250) => { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), ms); }; };

  /* ==================================
     Format money (cents → display)
     ================================== */
  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  document.addEventListener('DOMContentLoaded', () => {

    /* ==================================
       Page transitions
       ================================== */
    const main = qs('#MainContent');
    const mainChildren = qsa('#MainContent > *');
    const headerEl = qs('.site-header');
    [headerEl, ...mainChildren].forEach((el, idx) => {
      if (!el) return;
      el.classList.add('page-fade-in');
      el.style.setProperty('--motion-stagger', (idx * 60) + 'ms');
    });
    requestAnimationFrame(() => { document.body.classList.add('is-loaded'); });

    /* Link exit transitions */
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!prefersReduced) {
      document.addEventListener('click', (e) => {
        if (e.defaultPrevented) return; /* respect handlers that cancelled the click (e.g. cart drawer) */
        const a = e.target.closest('a[href]');
        if (!a) return;
        if (a.closest('[data-cart-toggle]') || a.hasAttribute('data-cart-toggle')) return; /* cart icon opens drawer only */
        const href = a.getAttribute('href');
        const target = a.getAttribute('target');
        const isModified = e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0;
        const isHash = href && href.startsWith('#');
        const isExternal = href && /^(https?:)?\/\//.test(href) && (new URL(href, window.location.origin)).origin !== window.location.origin;
        const noTrans = a.dataset.noTransition !== undefined;
        if (isModified || isHash || isExternal || target === '_blank' || noTrans) return;
        e.preventDefault();
        document.body.classList.add('is-exiting');
        setTimeout(() => { window.location.href = href; }, 440);
      });
    }

    /* Sticky header */
    const header = qs('.site-header');
    if (header) header.classList.add('sticky');

    /* ==================================
       Mobile nav
       ================================== */
    const navBtn = qs('[data-nav-toggle]');
    const navPanel = qs('#MobileNav');
    if (navBtn && navPanel) {
      const navBackdrop = qs('.mobile-nav__backdrop');
      const openNav = () => {
        navPanel.setAttribute('aria-hidden', 'false');
        navBtn.setAttribute('aria-expanded', 'true');
        navPanel.classList.add('is-open');
        navBackdrop?.classList.add('is-open');
        document.body.classList.add('no-scroll');
        trapFocus(navPanel);
      };
      const closeNav = () => {
        navPanel.setAttribute('aria-hidden', 'true');
        navBtn.setAttribute('aria-expanded', 'false');
        navPanel.classList.remove('is-open');
        navBackdrop?.classList.remove('is-open');
        document.body.classList.remove('no-scroll');
        releaseFocus(navPanel);
      };
      navBtn.addEventListener('click', openNav);
      navBackdrop?.addEventListener('click', closeNav);
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeNav(); });
    }

    /* ==================================
       Cart Drawer — Seamless
       ================================== */
    const cartToggle = qs('[data-cart-toggle]');
    const cartDrawer = qs('#CartDrawer');

    if (cartToggle && cartDrawer) {
      const panel = qs('.cart-drawer__panel', cartDrawer);

      const openDrawer = () => {
        cartDrawer.classList.add('is-open');
        document.body.classList.add('no-scroll');
        trapFocus(panel);
        /* Refresh cart data on open */
        fetchAndRenderCart();
      };
      const closeDrawer = () => {
        cartDrawer.classList.remove('is-open');
        document.body.classList.remove('no-scroll');
        releaseFocus(panel);
      };

      cartToggle.addEventListener('click', (e) => {
        e.preventDefault();
        openDrawer();
      });

      /* Close buttons */
      qsa('[data-close]', cartDrawer).forEach(btn => {
        btn.addEventListener('click', closeDrawer);
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeDrawer(); });

      /* Listen for cart:updated from quick-add */
      window.addEventListener('cart:updated', (e) => {
        renderCartDrawer(e.detail);
        openDrawer();
      });

      /* === Cart AJAX helpers === */
      async function fetchAndRenderCart() {
        try {
          const res = await fetch('/cart.js');
          const cart = await res.json();
          renderCartDrawer(cart);
        } catch(err) { /* silent */ }
      }

      function renderCartDrawer(cart) {
        const container = qs('#CartDrawerItems', cartDrawer);
        const footer = qs('#CartDrawerFooter', cartDrawer);
        const totalEl = qs('#CartDrawerTotal', cartDrawer);
        const countEl = qs('#cart-count');

        if (countEl) countEl.textContent = cart.item_count;

        if (!container) return;

        if (cart.item_count === 0) {
          container.innerHTML = '<div class="cart-drawer__empty"><p>Your cart is empty.</p><a href="/collections/legacy-blends" class="hog-btn hog-btn--gold" data-no-transition>Shop Our Roasts</a></div>';
          if (footer) footer.style.display = 'none';
          return;
        }

        if (footer) footer.style.display = '';
        if (totalEl) totalEl.textContent = formatMoney(cart.total_price);

        container.innerHTML = cart.items.map((item, idx) => {
          const line = idx + 1;
          const variantLabel = item.variant_title && item.variant_title !== 'Default Title'
            ? '<span class="cart-drawer__item-variant">' + item.variant_title + '</span>' : '';
          return '<div class="cart-drawer__item" data-line="' + line + '">'
            + '<a href="' + item.url + '" class="cart-drawer__item-img">'
            + (item.image ? '<img src="' + item.image + '" alt="" loading="lazy">' : '')
            + '</a>'
            + '<div class="cart-drawer__item-details">'
            + '<a href="' + item.url + '" class="cart-drawer__item-title">' + item.product_title + '</a>'
            + variantLabel
            + '<span class="cart-drawer__item-price">' + formatMoney(item.final_line_price) + '</span>'
            + '<div class="cart-drawer__item-controls">'
            + '<div class="cart-drawer__qty">'
            + '<button class="cart-drawer__qty-btn" data-line-minus="' + line + '" aria-label="Decrease">−</button>'
            + '<input type="number" class="cart-drawer__qty-input" value="' + item.quantity + '" min="0" data-line-qty="' + line + '">'
            + '<button class="cart-drawer__qty-btn" data-line-plus="' + line + '" aria-label="Increase">+</button>'
            + '</div>'
            + '<button class="cart-drawer__remove" data-line-remove="' + line + '" aria-label="Remove">'
            + '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 4h10M5 4V2.5a.5.5 0 01.5-.5h3a.5.5 0 01.5.5V4m1.5 0v7.5a1 1 0 01-1 1h-5a1 1 0 01-1-1V4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>'
            + '</button>'
            + '</div></div></div>';
        }).join('');
      }

      /* Debounced line update */
      const updateLine = debounce(async (line, quantity) => {
        try {
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ line: line, quantity: quantity })
          });
          const cart = await res.json();
          renderCartDrawer(cart);
          if (window.UncommonGrounds && window.UncommonGrounds.updateCartCount) {
            window.UncommonGrounds.updateCartCount();
          }
        } catch(err) { /* silent */ }
      }, 200);

      /* +/- and remove event delegation */
      cartDrawer.addEventListener('click', (e) => {
        const minus = e.target.closest('[data-line-minus]');
        const plus = e.target.closest('[data-line-plus]');
        const remove = e.target.closest('[data-line-remove]');

        if (minus) {
          const line = parseInt(minus.getAttribute('data-line-minus'));
          const input = qs('[data-line-qty="' + line + '"]', cartDrawer);
          const val = input ? parseInt(input.value) : 1;
          const newVal = Math.max(0, val - 1);
          if (input) input.value = newVal;
          updateLine(line, newVal);
        }
        if (plus) {
          const line = parseInt(plus.getAttribute('data-line-plus'));
          const input = qs('[data-line-qty="' + line + '"]', cartDrawer);
          const val = input ? parseInt(input.value) : 1;
          if (input) input.value = val + 1;
          updateLine(line, val + 1);
        }
        if (remove) {
          const line = parseInt(remove.getAttribute('data-line-remove'));
          const item = remove.closest('.cart-drawer__item');
          if (item) { item.style.opacity = '0'; item.style.transform = 'translateX(20px)'; }
          setTimeout(() => updateLine(line, 0), 200);
        }
      });

      /* Direct qty input */
      cartDrawer.addEventListener('input', (e) => {
        const el = e.target;
        if (el.matches('[data-line-qty]')) {
          const line = parseInt(el.getAttribute('data-line-qty'));
          const qty = parseInt(el.value) || 0;
          updateLine(line, qty);
        }
      });
    }

    /* ==================================
       Collection filter chip bridge
       ================================== */
    const roastSelect = qs('#RoastFilter');
    const grindSelect = qs('#GrindFilter');
    document.addEventListener('click', (e) => {
      const r = e.target.closest('[data-filter-roast]');
      const g = e.target.closest('[data-filter-grind]');
      if (r && roastSelect) { roastSelect.value = r.getAttribute('data-filter-roast'); roastSelect.dispatchEvent(new Event('change')); }
      if (g && grindSelect) { grindSelect.value = g.getAttribute('data-filter-grind'); grindSelect.dispatchEvent(new Event('change')); }
    });

    /* ==================================
       Scroll Reveal — staggered
       ================================== */
    if ('IntersectionObserver' in window) {
      const revealSections = qsa('[data-reveal]');
      const sectionIO = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            /* Stagger children */
            const children = qsa('[data-reveal-child]', entry.target);
            children.forEach((child, i) => {
              child.style.transitionDelay = (i * 80) + 'ms';
              child.classList.add('is-revealed');
            });
            sectionIO.unobserve(entry.target);
          }
        });
      }, { threshold: 0.08, rootMargin: '0px 0px -8% 0px' });
      revealSections.forEach(s => sectionIO.observe(s));

    /* Immediate viewport check: reveal sections already visible on load */
    requestAnimationFrame(() => {
      setTimeout(() => {
        revealSections.forEach(s => {
          const rect = s.getBoundingClientRect();
          const vh = window.innerHeight || document.documentElement.clientHeight;
          if (rect.top < vh && rect.bottom > 0) {
            s.classList.add('is-revealed');
            const children = qsa('[data-reveal-child]', s);
            children.forEach((child, i) => {
              child.style.transitionDelay = (i * 80) + 'ms';
              child.classList.add('is-revealed');
            });
            sectionIO.unobserve(s);
          }
        });
      }, 100);
    });

      /* Also observe individual motion.css elements that need is-revealed class */
    const motionElements = qsa('.scroll-reveal, .hog-card, .hog-process__step, .hog-ws-details__card, .hog-ws-details__stat');
    const elementIO = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          elementIO.unobserve(entry.target);
        }
      });
    }, { threshold: 0.05, rootMargin: '0px 0px -5% 0px' });
    motionElements.forEach(el => {
      if (!el.classList.contains('is-revealed')) {
        elementIO.observe(el);
      }
    });
    
    /* Immediate check: reveal motion elements already in viewport */
    requestAnimationFrame(() => {
      setTimeout(() => {
        motionElements.forEach(el => {
          if (!el.classList.contains('is-revealed')) {
            const rect = el.getBoundingClientRect();
            const vh = window.innerHeight || document.documentElement.clientHeight;
            if (rect.top < vh && rect.bottom > 0) {
              el.classList.add('is-revealed');
              elementIO.unobserve(el);
            }
          }
        });
      }, 150);
    });

    
    /* Also observe .fade-in (legacy) */
      const fadeIns = qsa('.shopify-section');
      fadeIns.forEach(s => s.classList.add('fade-in'));
      const fadeIO = new IntersectionObserver((entries) => {
        entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-visible'); fadeIO.unobserve(e.target); } });
      }, { threshold: 0.12 });
      fadeIns.forEach(t => fadeIO.observe(t));
    }

    /* ==================================
       PDP: grind selector
       ================================== */
    const variantSelect = qs('.product-variant-select');
    if (variantSelect) {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-grind-value]');
        if (!btn) return;
        const grind = btn.getAttribute('data-grind-value');
        const opts = Array.from(variantSelect.options);
        const match = opts.find(o => o.textContent.toLowerCase().includes(grind.toLowerCase()));
        if (match) { variantSelect.value = match.value; variantSelect.dispatchEvent(new Event('change')); }
      });
    }

    /* PDP: subscription toggle */
    const subToggle = qs('#SubToggle');
    const priceWrap = qs('.product-price-wrapper');
    if (subToggle && priceWrap) {
      subToggle.addEventListener('change', () => {
        priceWrap.classList.toggle('is-subscribed', subToggle.checked);
      });
    }

    /* PDP: gallery thumbnails — handled by picture-aware script in
       templates/product.liquid (legacy 100x100 URL swap retired). */

    /* ==================================
       Sticky hero → smooth handoff
       ================================== */
    const hero = qs('.hero-section');
    if (hero) {
      const heroWrap = hero.closest('.shopify-section');
      const nextWrap = heroWrap?.nextElementSibling?.classList.contains('shopify-section')
        ? heroWrap.nextElementSibling : null;
      if (heroWrap && nextWrap) {
        heroWrap.classList.add('sticky-hero');
        nextWrap.classList.add('sticky-hero-next');
        if (!prefersReduced) {
          let ticking = false;
          const easeInOutCubic = (p) => p < 0.5 ? 4 * p * p * p : 1 - Math.pow(-2 * p + 2, 3) / 2;
          const computeProgress = () => {
            const rect = nextWrap.getBoundingClientRect();
            const vh = window.innerHeight || document.documentElement.clientHeight;
            const overlap = Math.max(0, vh - rect.top);
            const progress = Math.min(1, overlap / vh);
            const p = easeInOutCubic(progress).toFixed(3);
            heroWrap.style.setProperty('--handoff', p);
            nextWrap.style.setProperty('--handoff', p);
          };
          const onScrollOrResize = () => {
            if (ticking) return;
            ticking = true;
            requestAnimationFrame(() => { computeProgress(); ticking = false; });
          };
          computeProgress();
          window.addEventListener('scroll', onScrollOrResize, { passive: true });
          window.addEventListener('resize', onScrollOrResize);
        } else {
          heroWrap.style.setProperty('--handoff', '1');
          nextWrap.style.setProperty('--handoff', '1');
        }
      }
    }

  });
})();

/* ===============================
   Our Story scroll reveal
   =============================== */
(function () {
  var revealables = document.querySelectorAll(
    '.story-hero h1, .story-hero p, ' +
    '.story-container h2, .story-container p, ' +
    '.story-cta h2, .story-cta p, .story-cta a'
  );
  if (!revealables.length) return;
  revealables.forEach(function(el) { el.classList.add('reveal'); });
  if (!('IntersectionObserver' in window)) {
    revealables.forEach(function(el) { el.classList.add('is-visible'); });
    return;
  }
  var io = new IntersectionObserver(function(entries) {
    entries.forEach(function(entry) {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
    });
  }, { threshold: 0.15, rootMargin: '0px 0px -10% 0px' });
  revealables.forEach(function(el) { io.observe(el); });
})();
